import { create } from 'zustand'
import * as Sentry from '@sentry/react'
import { toast } from 'react-toastify'
import i18n from '../i18n'
import {
  deleteNotification,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../api/notifications'
import {
  applyDeleted,
  applyNew,
  applyRead,
  applyReadAll,
  markReadListStale,
  mergeFirstPage,
} from '../lib/realtime/notificationListMerge'

// One store for a branch's notifications, shared by the bell dropdown in
// both shells (which remount on every route change and so cannot own this
// state) and the history page. A notification belongs to the tenant
// (branch): its read state is the same for every staff member.
//
// There is one list per history-page tab. `all` loads as soon as a branch is
// ready (the bell reads its head); `unread` and `read` load when their tab is
// first shown.
// growchat-doc/manager-notification-page/spec-manager-notification-page.md

export const NOTIFICATION_PAGE_SIZE = 20

// `generation` changes whenever a list's rows are replaced wholesale, so a
// "load more" answer for the rows that were there before can be recognised
// and dropped. `stale` asks for a reload the next time the tab is shown.
const emptyList = () => ({
  items: [],
  loaded: false,
  status: 'idle',
  nextCursor: null,
  loadingMore: false,
  stale: false,
  generation: 0,
})

const initialState = () => ({
  tenantId: null,
  unreadCount: 0,
  lists: { all: emptyList(), unread: emptyList(), read: emptyList() },
})

const EMPTY_VIEW = Object.freeze({
  items: [],
  unreadCount: 0,
  status: 'idle',
  hasMore: false,
  loadingMore: false,
  stale: false,
})

// What a screen showing `tenantId` may display. While the store still holds
// another branch's data (mid-switch), that is nothing — never the old rows.
// Returns a fresh object: select single fields from it, or wrap the selector
// in `useShallow`.
export function selectTenantNotifications(state, tenantId, filter = 'all') {
  if (!tenantId || state.tenantId !== tenantId) return EMPTY_VIEW
  const list = state.lists[filter]
  return {
    items: list.items,
    unreadCount: state.unreadCount,
    status: list.status,
    hasMore: Boolean(list.nextCursor),
    loadingMore: list.loadingMore,
    stale: list.stale,
  }
}

function listParams(filter, cursor) {
  return {
    limit: NOTIFICATION_PAGE_SIZE,
    ...(filter !== 'all' && { filter }),
    ...(cursor && { cursor }),
  }
}

function eventTenantId(event) {
  return event?.tenant_id ?? event?.notification?.tenant_id ?? null
}

const useNotificationStore = create((set, get) => {
  const setList = (filter, patch) =>
    set((state) => ({
      lists: { ...state.lists, [filter]: { ...state.lists[filter], ...patch } },
    }))

  // `merge` folds the page into the rows already shown; otherwise it
  // replaces them. Either way the response is dropped if the branch changed
  // while it was in flight.
  const loadFirstPage = async (filter, { merge = false, source = 'unknown' } = {}) => {
    const tenantId = get().tenantId
    if (!tenantId) return
    const wasLoaded = get().lists[filter].loaded
    if (!wasLoaded) setList(filter, { status: 'loading' })
    try {
      const data = await listNotifications(tenantId, listParams(filter))
      if (get().tenantId !== tenantId) return
      const page = data?.notifications || []
      set((state) => {
        const list = state.lists[filter]
        const keepRows = merge && list.loaded
        return {
          unreadCount: data?.unread_count || 0,
          lists: {
            ...state.lists,
            [filter]: {
              ...list,
              items: keepRows ? mergeFirstPage(list.items, page) : page,
              loaded: true,
              status: 'loaded',
              stale: false,
              nextCursor: keepRows ? list.nextCursor : data?.next_cursor ?? null,
              loadingMore: keepRows ? list.loadingMore : false,
              generation: keepRows ? list.generation : list.generation + 1,
            },
          },
        }
      })
    } catch {
      if (get().tenantId !== tenantId) return
      if (!wasLoaded) setList(filter, { status: 'error' })
      Sentry.addBreadcrumb({ message: 'Notification fetch failed', level: 'warning', data: { source, tenantId } })
    }
  }

  // Lists never shown stay unloaded; one whose first load is still in
  // flight is about to receive current data anyway.
  const reloadLoadedLists = (source) =>
    Promise.all(
      Object.keys(get().lists)
        .filter((filter) => !['idle', 'loading'].includes(get().lists[filter].status))
        .map((filter) => loadFirstPage(filter, { source }))
    )

  // The change is shown at once; the server call follows. "Not found" means
  // a colleague already did it — shared read state makes that a success.
  // Any other failure is reported and the lists are reloaded to undo the
  // optimistic change.
  // `confirmed`, when given, runs once the server has accepted the change.
  const act = async (action, optimistic, request, { extra, confirmed } = {}) => {
    const tenantId = get().tenantId
    if (!tenantId) return
    set((state) => optimistic(state))
    try {
      await request(tenantId)
      if (confirmed && get().tenantId === tenantId) confirmed()
    } catch (e) {
      const status = e?.response?.status
      if (status === 404) return
      if (!e?.response || status >= 500) {
        Sentry.captureException(e, { tags: { action }, extra: { tenantId, ...extra } })
      }
      if (get().tenantId !== tenantId) return
      toast.error(i18n.t(status === 403 ? 'notifications.noPermission' : 'notifications.actionFailed'))
      await reloadLoadedLists(action)
    }
  }

  return {
    ...initialState(),

    reset: () => set(initialState()),

    setTenant: async (tenantId) => {
      const next = tenantId || null
      if (get().tenantId === next) return
      set({ ...initialState(), tenantId: next })
      await loadFirstPage('all', { source: 'tenant' })
    },

    // The realtime session (re)opened: events may have been missed, so what
    // is on screen is replaced with what the server has now.
    resync: () => reloadLoadedLists('resync'),

    // The tab regained attention while the session stayed connected: nothing
    // was missed, so the latest page is folded in without cutting the list.
    refresh: (source = 'refresh') => loadFirstPage('all', { merge: true, source }),

    // Shows a history-page tab: loads it the first time, after a failed
    // load, and when a "mark all read" left it stale.
    showTab: async (filter) => {
      const list = get().lists[filter]
      if (!list || !get().tenantId) return
      if (list.status === 'idle' || list.status === 'error' || list.stale) {
        await loadFirstPage(filter, { source: 'tab' })
      }
    },

    loadMore: async (filter) => {
      const tenantId = get().tenantId
      const list = get().lists[filter]
      if (!tenantId || !list?.loaded || !list.nextCursor || list.loadingMore) return
      const { generation } = list
      const stillCurrent = () => get().tenantId === tenantId && get().lists[filter].generation === generation
      setList(filter, { loadingMore: true })
      try {
        const data = await listNotifications(tenantId, listParams(filter, list.nextCursor))
        if (!stillCurrent()) return
        set((state) => {
          const current = state.lists[filter]
          const known = new Set(current.items.map((n) => n.id))
          const added = (data?.notifications || []).filter((n) => !known.has(n.id))
          return {
            unreadCount: data?.unread_count ?? state.unreadCount,
            lists: {
              ...state.lists,
              [filter]: {
                ...current,
                items: [...current.items, ...added],
                nextCursor: data?.next_cursor ?? null,
                loadingMore: false,
              },
            },
          }
        })
      } catch {
        if (!stillCurrent()) return
        setList(filter, { loadingMore: false })
        toast.error(i18n.t('notifications.loadFailed'))
        Sentry.addBreadcrumb({ message: 'Notification load-more failed', level: 'warning', data: { tenantId, filter } })
      }
    },

    markRead: (id) => {
      const alreadyRead = Object.values(get().lists).some((list) =>
        list.items.some((n) => n.id === id && n.read)
      )
      if (alreadyRead) return Promise.resolve()
      return act(
        'mark_notification_read',
        (state) => applyRead(state, id),
        (tenantId) => markNotificationRead(tenantId, id),
        { extra: { notificationId: id } }
      )
    },

    markAllRead: () =>
      act(
        'mark_all_notifications_read',
        (state) => applyReadAll(state),
        (tenantId) => markAllNotificationsRead(tenantId),
        // The Read tab now has rows this screen may never have loaded. The
        // realtime echo flags it too; this covers a session without one.
        { confirmed: () => set((state) => ({ lists: markReadListStale(state.lists) })) }
      ),

    remove: (id) =>
      act(
        'delete_notification',
        (state) => applyDeleted(state, id),
        (tenantId) => deleteNotification(tenantId, id),
        { extra: { notificationId: id } }
      ),

    applyEvent: (event) => {
      const tenantId = get().tenantId
      if (!tenantId || eventTenantId(event) !== tenantId) return
      // The read-state events carry the tenant's unread total counted after
      // the write; it is taken as is. They also echo back to the screen that
      // made the change, where applying them again changes nothing.
      const count = event.unread_count
      if (event.type === 'notification.new') set((state) => applyNew(state, event.notification))
      else if (event.type === 'notification.read') set((state) => applyRead(state, event.notification_id, count))
      else if (event.type === 'notification.read_all') set((state) => applyReadAll(state, event.read_before, count))
      else if (event.type === 'notification.deleted') set((state) => applyDeleted(state, event.notification_id, count))
    },
  }
})

export default useNotificationStore
